/*
 * Copyright (C) 2009-2025 SonarSource Sàrl
 * All rights reserved
 * mailto:info AT sonarsource DOT com
 */

import { Spinner, Text, toast, ToastDuration, ToggleTip } from '@sonarsource/echoes-react';
import { FormattedMessage } from 'react-intl';
import { Switch } from '~adapters/components/common/Switch';
import useLocalStorage from '~shared/helpers/useLocalStorage';
import { usePatchJiraBindingPropertiesMutation } from '../../queries';
import { JiraOrganizationBinding } from '../../types';

interface OrganizationJiraGrantPermissionsSwitchProps {
  binding: JiraOrganizationBinding;
  organizationKey: string;
}

export default function OrganizationJiraGrantPermissionsSwitch({
  binding,
  organizationKey,
}: Readonly<OrganizationJiraGrantPermissionsSwitchProps>) {
  const { mutate: patchProperties, isPending: isPatchingBinding } =
    usePatchJiraBindingPropertiesMutation();

  const [_, setIsCalloutShown] = useLocalStorage(
    `jira-org-binding-callout_${organizationKey}`,
    false,
  );

  const handleProjectPermissionsToggle = (on: boolean) => {
    patchProperties(
      {
        sonarOrganizationUuid: binding.sonarOrganizationUuid,
        isTokenShared: on,
      },
      {
        onSuccess: () => {
          // Hide callout permanently on user action
          setIsCalloutShown(false);

          toast.success({
            duration: ToastDuration.Short,
            title: (
              <FormattedMessage
                id={
                  on
                    ? 'organization.jira.binding.connected.grant_project_permissions.toast_granted.title'
                    : 'organization.jira.binding.connected.grant_project_permissions.toast_revoked.title'
                }
              />
            ),
            description: (
              <FormattedMessage
                id={
                  on
                    ? 'organization.jira.binding.connected.grant_project_permissions.toast_granted.description'
                    : 'organization.jira.binding.connected.grant_project_permissions.toast_revoked.description'
                }
              />
            ),
          });
        },
      },
    );
  };

  return (
    <div className="sw-flex sw-items-center">
      <Switch
        ariaLabelledBy="grant-project-permissions-label"
        onChange={handleProjectPermissionsToggle}
        value={!!binding.isTokenShared}
      />
      <Text className="sw-ml-3" id="grant-project-permissions-label" isHighlighted>
        <FormattedMessage id="organization.jira.binding.connected.grant_project_permissions.label" />
      </Text>
      <ToggleTip
        className="sw-ml-1"
        description={
          <>
            <FormattedMessage id="organization.jira.binding.connected.grant_project_permissions.description" />

            <p className="sw-mt-2">
              <FormattedMessage id="organization.jira.binding.connected.grant_project_permissions.description_2" />
            </p>
          </>
        }
      />
      <Spinner className="sw-ml-2" isLoading={isPatchingBinding} />
    </div>
  );
}
